import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { buildAttributionEnvelope } from '../lib/attribution';
import { applyUserLanguage, SUPPORTED_LANGUAGES } from '../i18n';

const AuthContext = createContext(null);

/**
 * Phase 48 · Merge the anonymous support-chat conversation into the
 * user's account right after login/verify. Best-effort — we swallow
 * errors so a failed merge never blocks authentication.
 */
async function _mergeAnonChat() {
  try {
    const anonId = localStorage.getItem('nymia_anon_chat_sid');
    if (!anonId) return;
    await api.post('/help/chat/merge-anon', { anon_session_id: anonId });
  } catch (_e) { /* silent */ }
}

/**
 * App → Web handoff: `?handoff=<code>` (minted by POST /auth/handoff from the
 * mobile app) is exchanged ONCE for a JWT. Module-level promise so a second
 * effect run (React StrictMode) awaits the same in-flight exchange instead of
 * seeing the stripped URL and treating the visitor as anonymous.
 * Resolves with the exchange payload or null (no code / failure → normal login).
 */
let _handoffExchange = null;
function _consumeHandoffOnce() {
  if (_handoffExchange) return _handoffExchange;
  const params = new URLSearchParams(window.location.search);
  const code = params.get('handoff');
  if (!code) return Promise.resolve(null);
  params.delete('handoff');
  const qs = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  _handoffExchange = api.post('/auth/handoff/exchange', { code })
    .then((res) => res.data)
    .catch(() => null);
  return _handoffExchange;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const consumeHandoff = useCallback(async () => {
    const data = await _consumeHandoffOnce();
    if (!data) return false;
    localStorage.setItem('ig_token', data.token);
    // Deep link back to the mobile app (shown as "Torna all'app" CTA).
    sessionStorage.setItem('nymia_from_app', '1');
    if (data.return_url) sessionStorage.setItem('nymia_app_return_url', data.return_url);
    else sessionStorage.removeItem('nymia_app_return_url');
    setUser(data.user);
    if (data.user?.preferred_language) {
      applyUserLanguage(data.user.preferred_language);
    }
    return true;
  }, []);

  const loadUser = useCallback(async () => {
    if (await consumeHandoff()) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('ig_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      // Phase i18n · sync UI language with the authenticated profile.
      if (res.data?.preferred_language) {
        applyUserLanguage(res.data.preferred_language);
      }
    } catch (e) {
      localStorage.removeItem('ig_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [consumeHandoff]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (identifier, password) => {
    // Phase 37F · attach marketing attribution envelope (utm_*, fbclid,
    // gclid, referrer, meta cookies). Backend uses it to update
    // last_touch (never first_touch) and to enrich Meta CAPI events.
    const attribution = buildAttributionEnvelope();
    const body = attribution ? { identifier, password, attribution } : { identifier, password };
    const res = await api.post('/auth/login', body);
    localStorage.setItem('ig_token', res.data.token);
    setUser(res.data.user);
    // Phase i18n · align UI language to the fetched profile as soon as
    // the session is established (post-login redirect happens next).
    if (res.data.user?.preferred_language) {
      applyUserLanguage(res.data.user.preferred_language);
    }
    // Phase 48 · link any pre-login support chat to the new session.
    _mergeAnonChat();
    return res.data.user;
  };

  const register = async (payload) => {
    // Phase 27.5 — registration no longer issues a JWT. The backend
    // returns `{verification_required, email, username, user_id}`.
    // The caller must redirect to /verify-email to obtain the token.
    // Phase 37F · always attach attribution envelope. First_touch is
    // set once here (immutable) so we permanently remember how the
    // user found Nymia.
    const attribution = buildAttributionEnvelope();
    const body = attribution ? { ...payload, attribution } : payload;
    const res = await api.post('/auth/register', body);
    return res.data;
  };

  // Phase 27.5 — public verification used right after registration or
  // when login is blocked by `email_verification_required`. On success
  // backend returns `{token, user}` so we can finalize the session.
  const verifyEmailPublic = async (email, otp) => {
    const res = await api.post('/auth/verify-email-public', { email, otp });
    localStorage.setItem('ig_token', res.data.token);
    setUser(res.data.user);
    // Phase 48 · merge anon chat after first-verify session bootstrap.
    _mergeAnonChat();
    return res.data.user;
  };

  const resendVerifyOtpPublic = async (email) => {
    await api.post('/auth/verify-email/resend-public', { email });
  };

  const logout = () => {
    localStorage.removeItem('ig_token');
    setUser(null);
  };

  const updateUser = async (data) => {
    const res = await api.patch('/auth/me', data);
    setUser(res.data);
    return res.data;
  };

  /**
   * Phase i18n · persist the caller's UI language.
   * - Applies the change locally immediately (i18next + localStorage).
   * - When authenticated, mirrors the choice on the backend profile so
   *   the same language follows the user across devices/browsers.
   */
  const setLanguage = useCallback(async (lang) => {
    if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) return;
    applyUserLanguage(lang);
    if (localStorage.getItem('ig_token')) {
      try {
        await api.put('/auth/me/language', { language: lang });
        setUser((prev) => (prev ? { ...prev, preferred_language: lang } : prev));
      } catch (err) {
        /* best-effort; local UI already updated */
        // eslint-disable-next-line no-console
        console.warn('[i18n] Failed to sync preferred_language to backend', err);
      }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, verifyEmailPublic, resendVerifyOtpPublic, logout, updateUser, setLanguage, refresh: loadUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
